import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Container, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../services/authService';

const ResetPassword: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [token, setToken] = useState('');

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tokenParam = searchParams.get('token');

        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setError('Hiányzó vagy érvénytelen token.');
        }
    }, [location]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmNewPassword) {
            setError('A jelszavak nem egyeznek.');
            return;
        }

        if (!token) {
            setError('Hiányzó token.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.resetPassword({
                token,
                newPassword,
                confirmNewPassword
            });
            setSuccess('A jelszó sikeresen megváltozott! Most már bejelentkezhet az új jelszavával.');
            // [Web UX 2. csomag, 2026-10-03] A belépő oldal valódi útvonala (a régi `/login` a nyilvános
            // nyitóoldalra esett, jelentés 3.1).
            setTimeout(() => navigate('/belepes'), 3000);
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Hiba történt a jelszó visszaállítása közben.'));
        } finally {
            setLoading(false);
        }
    };

    if (!token && !error) return null; // Wait for useEffect

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                    <Typography component="h1" variant="h5" align="center" gutterBottom>
                        Új jelszó megadása
                    </Typography>

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!success && (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="newPassword"
                                label="Új jelszó"
                                type={showPassword ? 'text' : 'password'}
                                id="newPassword"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => { setNewPassword(e.target.value); if (error) setError(''); }}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmNewPassword"
                                label="Új jelszó megerősítése"
                                type={showPassword ? 'text' : 'password'}
                                id="confirmNewPassword"
                                autoComplete="new-password"
                                value={confirmNewPassword}
                                onChange={(e) => { setConfirmNewPassword(e.target.value); if (error) setError(''); }}
                                disabled={loading}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                // Csak akkor véglegesen tiltott, ha nincs token (értelmetlen a küldés).
                                // Egy visszaállítható hiba (pl. nem egyező jelszó) NEM tilthatja le
                                // örökre — a mezők onChange-e törli az error-t, így a gomb újra él.
                                disabled={loading || !token}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Jelszó mentése'}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default ResetPassword;
